import { KustomerMainWindow } from 'core/types';
import { installPolyfills } from 'polyfills/helpers';
import { fetchSettings } from 'core_main/api/settings';
import { init as sdkInit } from 'core_main/sdk/init';
import { createConversation as sdkCreateConversation } from 'core_main/sdk/create_conversation';
import { handoff as sdkHandoff } from 'core_main/sdk/handoff';
import { removeListener as sdkRemoveListener } from 'core_main/sdk/remove_listener';
import { addListener as sdkAddListener } from 'core_main/sdk/add_listener';
import { sendMessage as sdkSendMessage } from 'core_main/sdk/send_message';
import { getConversations as sdkGetConversations } from 'core_main/sdk/get_conversations';
import { getConversation as sdkGetConversation } from 'core_main/sdk/get_conversation';
import { getMessages as sdkGetMessages } from 'core_main/sdk/get_messages';
import { getHistoricalMessages as sdkGetHistoricalMessages } from 'core_main/sdk/get_historical_messages';
import { getAssistant as sdkGetAssistant } from 'core_main/sdk/get_assistant';
import {
  markRead as sdkMarkRead,
  markSessionAsRead as sdkMarkSessionAsRead,
} from 'core_main/sdk/mark_read';
import { getUnreadCount as sdkGetUnreadCount } from 'core_main/sdk/get_unread_count';
import { endConversation as sdkEndConversation } from 'core_main/sdk/end_conversation';
import { initAssistant as sdkInitAssistant } from 'core_main/sdk/init_assistant';
import { sendKbDeflectionClick as sdkSendKbDeflectionClick } from 'core_main/sdk/send_kb_deflection_click';
import { isChatAvailable as sdkIsChatAvailable } from 'core_main/sdk/is_chat_available';
import { submitSatisfactionForm as sdkSubmitSatisfactionForm } from 'core_main/sdk/submit_satisfaction_form';
import { getTranslatedSatisfactionFormSdk } from 'core_main/sdk/get_translated_satisfaction_form';
import { sendTypingActivity as sdkSendTypingActivity } from 'core_main/sdk/send_typing_activity';
import { login as sdkLogin } from 'core_main/sdk/login';
import { isLoggedIn as sdkIsLoggedIn } from 'core_main/sdk/is_logged_in';
import { describeCustomer as sdkDescribeCustomer } from 'core_main/sdk/describe_customer';
import { describeConversation as sdkDescribeConversation } from 'core_main/sdk/describe_conversation';
import { getCategories as sdkGetCategories } from 'core_main/sdk/get_categories';
import { getArticleById as sdkGetArticleById } from 'core_main/sdk/get_article_by_id';
import { getArticleBySlug as sdkGetArticleBySlug } from 'core_main/sdk/get_article_by_slug';
import { submitArticleFeedback as sdkSubmitArticleFeedback } from 'core_main/sdk/submit_article_feedback';
import { updateArticleFeedback as sdkUpdateArticleFeedback } from 'core_main/sdk/update_article_feedback';
import { getArticles as sdkGetArticles } from 'core_main/sdk/get_articles';
import { getFeaturedArticles as sdkGetFeaturedArticles } from 'core_main/sdk/get_featured_articles';
import { getChatSettings as sdkGetChatSettings } from 'core_main/sdk/get_chat_settings';
import { getKnowledgeBaseConfig as sdkGetKnowledgeBaseConfig } from 'core_main/sdk/get_knowledge_base_config';
import { searchArticles as sdkSearchArticles } from 'core_main/sdk/search_articles';
import { isUrlAllowed as sdkIsUrlAllowed } from 'core_main/sdk/is_url_allowed';
import { logout as sdkLogout } from 'core_main/sdk/logout';
import { sendPresenceActivity as sdkSendPresenceActivity } from 'core_main/sdk/send_presence_activity';
import { stop as sdkStop } from 'core_main/sdk/stop';

import {
  getSafeParentContext,
  applyNonceToDynamicScripts,
} from 'helpers/browser';
import { KustomerErrorResponse } from 'globals/types';
import {
  GetFeaturedArticlesCallbackResponse,
  GetFeaturedArticlesParameters,
} from 'core_main/sdk/get_featured_articles/types';
import { getNonce } from 'helpers/nonce/helpers';
import { GetMessagesParameters } from './sdk/get_messages/types';
import { KustomerEventTypes } from './types';
import { MarkReadParameters } from './sdk/mark_read/types';
import { GetConversationParameters } from './sdk/get_conversation/types';
import { GetConversationsParameters } from './sdk/get_conversations/types';
import { SendMessageParameters } from './sdk/send_message/types';
import { EndConversationParameters } from './sdk/end_conversation/types';
import { SubmitSatisfactionFormParameters } from './sdk/submit_satisfaction_form/types';
import { SendTypingActivityParameters } from './sdk/send_typing_activity/types';
import { LoginParameters } from './sdk/login/types';
import { IsLoggedInParameters } from './sdk/is_logged_in/types';
import { DescribeCustomerParameters } from './sdk/describe_customer/types';
import { DescribeConversationParameters } from './sdk/describe_conversation/types';
import { CreateConversationParameters } from './sdk/create_conversation/types';
import { HandoffConversationParameters } from './sdk/handoff/types';
import {
  GetArticlesCallbackResponse,
  GetArticlesParameters,
  GetArticleByIdParameters,
  ArticleCallbackResponse,
  GetArticleBySlugParameters,
} from './sdk/get_articles/types';
import { SearchArticlesParameters } from './sdk/search_articles/types';
import {
  InitAssistantParameters,
  SendKbDeflectionClickParameters,
} from './api/assistant/types';
import {
  GetCategoriesCallbackResponse,
  GetCategoriesParameters,
} from './sdk/get_categories/types';
import { IsUrlAllowedParameters } from './sdk/is_url_allowed/types';
import { InitParameters } from './sdk/init/types';
import { SendPresenceActivityParameters } from './sdk/send_presence_activity/types';
import { GetAssistantParameters } from './sdk/get_assistant/types';
import { GetKnowledgeBaseConfigCallbackResponse } from './sdk/get_knowledge_base_config/types';
import { SubmitArticleFeedbackParameters } from './sdk/submit_article_feedback/types';
import { UpdateArticleFeedbackParameters } from './sdk/update_article_feedback/types';
import { ArticleFeedbackResponseData } from './api/articles/types';
import {
  countEvent,
  timeEventStart,
  timeEventStop,
} from '../../globals/eventTracking/helpers';
import {
  CountEventOptions,
  TimeEventOptions,
} from '../../globals/eventTracking/types';

const setupWindow = () => {
  const kustomerMainWindow: KustomerMainWindow = window;

  kustomerMainWindow.KustomerMain = {
    fetchSettings,
    init(parameters?: InitParameters, cb?) {
      sdkInit(parameters, cb);
    },
    createConversation(parameters: CreateConversationParameters, cb) {
      sdkCreateConversation(parameters, cb);
    },
    handoff(parameters: HandoffConversationParameters, cb) {
      return sdkHandoff(parameters, cb);
    },
    addListener(type: KustomerEventTypes, cb) {
      sdkAddListener(type, cb);
    },
    removeListener(type: KustomerEventTypes, cb) {
      sdkRemoveListener(type, cb);
    },
    sendMessage(parameters: SendMessageParameters, cb) {
      sdkSendMessage(parameters, cb);
    },
    getConversations(parameters: GetConversationsParameters, cb) {
      sdkGetConversations(parameters, cb);
    },
    getConversation(parameters: GetConversationParameters, cb) {
      sdkGetConversation(parameters, cb);
    },
    getMessages(parameters: GetMessagesParameters, cb) {
      sdkGetMessages(parameters, cb);
    },
    getHistoricalMessages(parameters: GetMessagesParameters, cb) {
      sdkGetHistoricalMessages(parameters, cb);
    },
    getAssistant(parameters: GetAssistantParameters) {
      return sdkGetAssistant(parameters);
    },
    markRead(parameters: MarkReadParameters) {
      sdkMarkRead(parameters);
    },
    markSessionAsRead(sessionId: string): Promise<void> {
      return sdkMarkSessionAsRead(sessionId);
    },
    getUnreadCount() {
      return sdkGetUnreadCount();
    },
    endConversation(parameters: EndConversationParameters, cb) {
      sdkEndConversation(parameters, cb);
    },
    initAssistant(parameters: InitAssistantParameters, cb) {
      return sdkInitAssistant(parameters, cb);
    },
    sendKbDeflectionClick(parameters: SendKbDeflectionClickParameters, cb) {
      return sdkSendKbDeflectionClick(parameters, cb);
    },
    isChatAvailable() {
      return sdkIsChatAvailable();
    },
    getChatSettings() {
      return sdkGetChatSettings();
    },
    submitSatisfactionForm(parameters: SubmitSatisfactionFormParameters, cb) {
      sdkSubmitSatisfactionForm(parameters, cb);
    },
    getTranslatedSatisfactionForm(
      parameters: { satisfactionId: string; lang?: string },
      cb,
    ) {
      getTranslatedSatisfactionFormSdk(parameters, cb);
    },
    sendTypingActivity(parameters: SendTypingActivityParameters) {
      sdkSendTypingActivity(parameters);
    },
    login(parameters?: LoginParameters, cb?) {
      sdkLogin(parameters, cb);
    },
    isLoggedIn(parameters: IsLoggedInParameters, cb) {
      sdkIsLoggedIn(parameters, cb);
    },
    describeCustomer(parameters: DescribeCustomerParameters, cb) {
      sdkDescribeCustomer(parameters, cb);
    },
    describeConversation(parameters: DescribeConversationParameters, cb) {
      sdkDescribeConversation(parameters, cb);
    },
    getCategories(
      parameters: GetCategoriesParameters,
      cb: (
        response: GetCategoriesCallbackResponse,
        error: KustomerErrorResponse,
      ) => void,
    ) {
      sdkGetCategories(parameters, cb);
    },
    getArticles(
      parameters: GetArticlesParameters,
      cb: (
        res: GetArticlesCallbackResponse,
        error: KustomerErrorResponse,
      ) => void,
    ) {
      sdkGetArticles(parameters, cb);
    },
    getFeaturedArticles(
      parameters: GetFeaturedArticlesParameters,
      cb: (
        res: GetFeaturedArticlesCallbackResponse,
        error: KustomerErrorResponse,
      ) => void,
    ) {
      sdkGetFeaturedArticles(parameters, cb);
    },
    getKnowledgeBaseConfig(
      cb?: (
        res: GetKnowledgeBaseConfigCallbackResponse,
        error: KustomerErrorResponse,
      ) => void,
    ) {
      return sdkGetKnowledgeBaseConfig(cb);
    },
    getArticleById(
      parameters: GetArticleByIdParameters,
      cb: (res: ArticleCallbackResponse | null, err?: Error) => void,
    ) {
      sdkGetArticleById(parameters, cb);
    },
    getArticleBySlug(
      parameters: GetArticleBySlugParameters,
      cb?: (res: ArticleCallbackResponse | null, err?: Error) => void,
    ) {
      return sdkGetArticleBySlug(parameters, cb);
    },
    submitArticleFeedback(
      parameters: SubmitArticleFeedbackParameters,
      cb: (res: ArticleFeedbackResponseData) => void,
    ) {
      return sdkSubmitArticleFeedback(parameters, cb);
    },
    updateArticleFeedback(
      parameters: UpdateArticleFeedbackParameters,
      cb: (res: ArticleFeedbackResponseData) => void,
    ) {
      return sdkUpdateArticleFeedback(parameters, cb);
    },
    searchArticles(parameters: SearchArticlesParameters, cb) {
      sdkSearchArticles(parameters, cb);
    },
    isUrlAllowed(parameters: IsUrlAllowedParameters) {
      return sdkIsUrlAllowed(parameters);
    },
    logout(cb) {
      sdkLogout(cb);
    },
    sendPresenceActivity(parameters: SendPresenceActivityParameters) {
      sdkSendPresenceActivity(parameters);
    },
    stop() {
      return sdkStop();
    },
    kustomerEventCount(parameters: CountEventOptions) {
      countEvent(parameters);
    },
    kustomerEventTimerStart(parameters: TimeEventOptions) {
      timeEventStart(parameters);
    },
    kustomerEventTimerStop(parameters: TimeEventOptions) {
      timeEventStop(parameters);
    },
  };

  const event = new CustomEvent('kustomer_core_main_ready');

  window.dispatchEvent(event);

  // If inside an iframe, dispatch the event to the parent window
  const parent = getSafeParentContext(window);
  if (parent && parent !== window) {
    parent.dispatchEvent(event);
  }
};

installPolyfills().then(() => {
  // Apply CSP nonce to dynamically loaded scripts
  /*
    Simplified nonce detection logic:
    - We now only look for a script tag with [data-kustomer-api-key],
      as all entry points (widget.js, core.js, widget_api) now include this attribute.
    The getNonce helper returns the nonce if present.
  */
  const scriptEmbedElement = document.querySelector(
    '[data-kustomer-api-key]',
  ) as HTMLScriptElement | null;
  const nonce = getNonce(scriptEmbedElement);
  if (nonce) {
    // Set up global interception to ensure all dynamically loaded scripts (this only includes resources from kustomer domains)
    // receive the correct CSP nonce. This is required for compliance with strict CSP policies.
    // Must be called before any dynamic imports or script loads occur.
    applyNonceToDynamicScripts(nonce);
  }

  setupWindow();
});
